import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import { AddButton } from "../../../components/common/AddButton";
import {
  DataTable,
  Column,
  Action,
  createColumn,
} from "../../../components/common/DataTable/DataTable";
import {
  useGetDetentionsQuery,
  useCreateDetentionMutation,
  useUpdateDetentionMutation,
  useUpdateDetentionStatusMutation,
  useDeleteDetentionMutation,
} from "../../../api/detectionsApi";
import {
  Detention,
  CreateDetentionDto,
  DetentionLumperStatus,
  DetentionLumperStatusLabel,
} from "../../../types/detention";
import DetentionFormModal from "../../detentions/components/DetentionFormModal";
import {
  calcDuration,
  createAmountColumn,
  createBrokerEmailColumn,
  createDateTimeColumn,
  createDetentionLumperSearchableColumns,
  createDocumentColumn,
  createEmailSentColumn,
  createLoadNumberColumn,
  createStatusColumn,
  createTrailerColumn,
  createVehicleColumn,
  filterDetentionLumperRecords,
} from "../utils/detentionLumperTableUtils";

const ALLOWED_ROLES = ["Admin", "Dispatcher"];
const DELETE_ROLES = ["Admin", "Dispatcher"];

const DetentionsTab: React.FC = () => {
  const authUser = useAppSelector((state) => state.auth.user);
  const canCreate = ALLOWED_ROLES.includes(authUser?.role || "");
  const canDelete = DELETE_ROLES.includes(authUser?.role || "");

  const { data: records = [], isLoading } = useGetDetentionsQuery();
  const [create, { isLoading: isCreating }] = useCreateDetentionMutation();
  const [update, { isLoading: isUpdating }] = useUpdateDetentionMutation();
  const [updateStatus] = useUpdateDetentionStatusMutation();
  const [remove, { isLoading: isDeleting }] = useDeleteDetentionMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<Detention | null>(null);
  const [feedback, setFeedback] = useState<{ severity: "success" | "error"; message: string } | null>(null);
  const [statusTarget, setStatusTarget] = useState<Detention | null>(null);
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({});

  const columns: Column[] = useMemo(
    () => [
      createLoadNumberColumn(),
      createBrokerEmailColumn(),
      createVehicleColumn(),
      createTrailerColumn(),
      createDateTimeColumn("timeIn", "Time In"),
      createDateTimeColumn("timeOut", "Time Out"),
      createColumn("duration", "Duration", {
        width: 100,
        renderCell: (_value, row: Detention) => (
          <Chip
            label={calcDuration(row.timeIn, row.timeOut)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      }),
      createAmountColumn(),
      createStatusColumn(),
      createEmailSentColumn(),
      createDocumentColumn(),
    ],
    []
  );

  const searchableColumns = useMemo(
    () => createDetentionLumperSearchableColumns(columns),
    [columns]
  );

  const filteredRecords = useMemo(
    () => filterDetentionLumperRecords(records, searchFilters),
    [records, searchFilters]
  );

  const getActions = useMemo(
    () =>
      (record: Detention): Action<Detention>[] => {
        const actions: Action<Detention>[] = [];

        if (canCreate) {
          actions.push(
            {
              icon: <EditIcon fontSize="small" />,
              label: "Edit",
              onClick: () => {
                setSelected(record);
                setIsFormOpen(true);
              },
              color: "primary",
            },
            {
              icon: <MoreVertIcon fontSize="small" />,
              label: "Change Status",
              onClick: (item) => setStatusTarget(item),
            }
          );
        }

        if (canDelete) {
          actions.push({
            icon: <DeleteIcon fontSize="small" />,
            label: "Delete",
            onClick: () => {
              setSelected(record);
              setIsDeleteOpen(true);
            },
            color: "error",
          });
        }

        return actions;
      },
    [canCreate, canDelete]
  );

  const handleSubmit = async (values: CreateDetentionDto & { status?: DetentionLumperStatus }, document?: File) => {
    try {
      if (selected) {
        await update({ id: selected.id, ...values }).unwrap();
        setFeedback({ severity: "success", message: "Detention updated." });
      } else {
        await create({ dto: values, document }).unwrap();
        const willSendEmail = values.amount != null && values.sendEmailToBroker;
        setFeedback({
          severity: "success",
          message: willSendEmail
            ? "Detention created and email sent to broker."
            : "Detention created.",
        });
      }
      setIsFormOpen(false);
      setSelected(null);
    } catch {
      setFeedback({ severity: "error", message: "Failed to save detention." });
    }
  };

  const handleStatusChange = async (record: Detention, status: DetentionLumperStatus) => {
    setStatusTarget(null);
    try {
      await updateStatus({ id: record.id, status }).unwrap();
      setFeedback({ severity: "success", message: `Status changed to ${DetentionLumperStatusLabel[status]}.` });
    } catch {
      setFeedback({ severity: "error", message: "Failed to update status." });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selected) return;
    try {
      await remove(selected.id).unwrap();
      setFeedback({ severity: "success", message: "Detention deleted." });
      setIsDeleteOpen(false);
      setSelected(null);
    } catch {
      setFeedback({ severity: "error", message: "Failed to delete detention." });
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      {feedback && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        </Box>
      )}

      {canCreate && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <AddButton onClick={() => { setSelected(null); setIsFormOpen(true); }}>
            Add Detention
          </AddButton>
        </Box>
      )}

      <DataTable<Detention>
        items={filteredRecords}
        columns={columns}
        actions={canCreate || canDelete ? getActions : undefined}
        isLoading={isLoading}
        totalCount={filteredRecords.length}
        fitContentHeight
        getRowId={(row) => row.id}
        enableSearch
        searchableColumns={searchableColumns}
        onSearchChange={setSearchFilters}
      />

      <Dialog open={Boolean(statusTarget)} onClose={() => setStatusTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Status</DialogTitle>
        <DialogContent sx={{ px: 0, pt: 0 }}>
          {([0, 1, 2] as DetentionLumperStatus[]).map((s) => (
            <ListItemButton
              key={s}
              selected={statusTarget?.status === s}
              onClick={() => statusTarget && handleStatusChange(statusTarget, s)}
            >
              <ListItemText primary={DetentionLumperStatusLabel[s]} />
            </ListItemButton>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <DetentionFormModal
        open={isFormOpen}
        onClose={() => { setIsFormOpen(false); setSelected(null); }}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
        initialValues={selected}
      />

      <Dialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Detention</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete detention for load #{selected?.loadNumber}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetentionsTab;
